import {
  TrendingUp,
  Landmark,
  Shield,
  Home,
  HeartHandshake,
  Infinity,
  Users,
  Award,
  Hourglass,
  Handshake,
} from "lucide-react";

export const CONTACT = {
  phone: "314-882-8565",
  phoneHref: "tel:+13148828565",
  email: "bb@breezewealthmanagement.com",
  instagram:
    "https://www.instagram.com/breezefinancialgroup?igsh=NHNtOG1xa3djY29q",
  linkedin: "https://www.linkedin.com/company/breezewm",
};

export const ASSETS = {
  breezeLogoBlue:
    "https://customer-assets.emergentagent.com/job_bac71456-3565-44fa-80bf-600161546337/artifacts/2uyaq1vf_IMG_7118.jpeg",
  bfgLogo:
    "https://customer-assets.emergentagent.com/job_bac71456-3565-44fa-80bf-600161546337/artifacts/svkf0dd8_IMG_7296.png",
  teamSuits: "/team-suits.jpg",
  teamWorking: "/team-working.jpg",
  familyBeach:
    "https://images.unsplash.com/photo-1709216461598-018ae6307dc0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHxoYXBweSUyMGZhbWlseSUyMGNhbG0lMjBuYXR1cmUlMjBzdW5zZXR8ZW58MHx8fHwxNzgxMTI2ODA5fDA&ixlib=rb-4.1.0&q=85",
  familyField:
    "https://images.unsplash.com/photo-1592711491694-e85f60a7773a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGZhbWlseSUyMGNhbG0lMjBuYXR1cmUlMjBzdW5zZXR8ZW58MHx8fHwxNzgxMTI2ODA5fDA&ixlib=rb-4.1.0&q=85",
};

export const PRODUCTS = [
  {
    id: "iul",
    tag: "IUL",
    name: "Indexed Universal Life",
    short: "Tax-free retirement income with no market downside risk. The hero product.",
    icon: TrendingUp,
    featured: true,
    accent: "breeze",
    keyNumbers: [
      { value: "0%", label: "downside risk" },
      { value: "Tax-free", label: "retirement income" },
      { value: "Lifetime", label: "protection" },
    ],
    detail:
      "An IUL grows your cash value based on the performance of a market index — with a floor of 0%, so you never lose money in a downturn. Build tax-free retirement income, protect your family with a death benefit, and access your money when life happens.",
    howItWorks:
      "Your premiums fund a permanent life insurance policy whose cash value is credited based on the performance of an underlying market index (like the S&P 500), subject to a cap on the upside and a floor of 0% on the downside. Once your cash value grows, you can borrow against it tax-free in retirement using policy loans — and whatever is left passes to your family income-tax-free.",
    whoFor: [
      "High earners maxing out their 401(k) and looking for the next tax-advantaged bucket",
      "Business owners and self-employed professionals without a corporate pension",
      "Parents who want death-benefit protection AND a tax-free retirement vehicle",
      "Anyone burned by a 401(k) drawdown who refuses to let the market dictate retirement",
    ],
    problems: [
      "401(k)s and IRAs are taxed when you withdraw — and tax rates may be higher later",
      "Market drawdowns near retirement can wipe out a decade of gains",
      "Roth limits cap how much high earners can shelter from future tax",
      "Term insurance protects your family but builds zero wealth",
    ],
    points: [
      "0% floor — your money never goes backward in a crash",
      "Tax-free income in retirement via policy loans",
      "Built-in death benefit protects your family from day one",
      "Living benefits for critical, chronic & terminal illness",
    ],
    funnel: {
      headline: ["Grow with the market.", "Never lose to it."],
      subhead:
        "An Indexed Universal Life policy links your cash value to a market index — with a floor, so a bad year can't take back what you've built. Tax-advantaged growth, lifelong protection, and access to your money when life happens.",
      pains: [
        {
          title: "Your 401(k) has a tax bill waiting.",
          body: "Every dollar you pull out in retirement is taxed as income — at whatever rate the future decides.",
        },
        {
          title: "One bad year can undo ten good ones.",
          body: "A market drop right before or during retirement doesn't just sting — it changes how long your money lasts.",
        },
        {
          title: "Term protects. It doesn't build.",
          body: "Term insurance covers your family if the worst happens. But when the term ends, so does everything you paid in.",
        },
      ],
      steps: [
        {
          title: "You fund the policy.",
          body: "Flexible premiums go into a permanent life insurance policy. Part covers your protection; the rest builds cash value.",
        },
        {
          title: "Your cash value tracks an index.",
          body: "Growth is credited based on an index like the S&P 500 — up to a cap in strong years, never below the floor in bad ones. You're never directly invested in the market.",
        },
        {
          title: "You access it tax-free. It grows tax-advantaged.",
          body: "In retirement, policy loans let you draw income without triggering income tax. Whatever remains passes to your family income-tax-free.",
        },
      ],
      faqs: [
        {
          q: "Isn't an IUL expensive?",
          a: "There are costs — insurance charges, fees, and the cost of the death benefit. What matters is whether the structure fits your goals. A well-designed IUL is built to minimize costs and maximize cash value; a poorly designed one does the opposite. That's why we walk through every line of the illustration with you before you decide.",
        },
        {
          q: "What's the catch with the cap?",
          a: "In a strong market year, your credited growth is limited to a cap set by the carrier. That's the trade-off for the floor: you give up some of the best years to never participate in the worst ones. For many families, sleeping through a downturn is worth it.",
        },
        {
          q: "Can I access the money before retirement?",
          a: "Yes. Once cash value has built up, you can take policy loans or withdrawals for things like a home, a business, or an emergency — without the age-based penalties of a 401(k) or IRA. Loans reduce your death benefit until repaid.",
        },
        {
          q: "What happens if I stop paying premiums?",
          a: "IULs have flexible premiums. If your cash value is sufficient, it can cover policy charges for a period. If it isn't, the policy can lapse — which is why we design funding around your real life, not a best-case scenario.",
        },
        {
          q: "How does this compare to a Roth IRA?",
          a: "Both offer tax-free growth potential. A Roth has strict contribution limits and income caps; an IUL doesn't, and it adds a death benefit plus living benefits. Many of our clients use both — the IUL becomes the next bucket once the Roth is maxed.",
        },
        {
          q: "Is my money actually in the stock market?",
          a: "No. Your cash value is never invested directly in the market. The carrier credits interest based on how an index performed — which is exactly what makes the floor possible.",
        },
      ],
    },
  },
  {
    id: "fia",
    tag: "FIA",
    name: "Fixed Indexed Annuities",
    short: "Guaranteed lifetime income. Protected from volatility.",
    icon: Landmark,
    featured: true,
    accent: "navy",
    keyNumbers: [
      { value: "Lifetime", label: "guaranteed income" },
      { value: "0%", label: "market loss exposure" },
      { value: "Index-linked", label: "growth potential" },
    ],
    detail:
      "A fixed indexed annuity converts your savings into income you can never outlive. Your principal is protected from market loss while still capturing index-linked growth — the retirement paycheck you can count on.",
    howItWorks:
      "You contribute a lump sum (often from a 401(k) or IRA rollover). The carrier credits interest tied to an index's performance — with a floor that protects your principal from loss. When you're ready, you switch on a guaranteed income rider that pays you a paycheck for life, no matter how long you live or how the market behaves.",
    whoFor: [
      "Pre-retirees (typically age 55–70) ready to de-risk their nest egg",
      "Anyone rolling over a 401(k) or old IRA who wants guaranteed income",
      "Couples worried one of them will outlive their savings",
      "Conservative savers tired of bond yields but unwilling to gamble on equities",
    ],
    problems: [
      "Outliving your savings is the #1 retirement fear — FIAs eliminate it",
      "Market volatility makes \"safe withdrawal rate\" rules unreliable",
      "Pensions are extinct for most workers — FIAs recreate that paycheck",
      "CDs and bonds barely keep up with inflation",
    ],
    points: [
      "Guaranteed lifetime income you can't outlive",
      "Principal protection — zero exposure to market loss",
      "Index-linked growth potential",
      "Ideal for 401(k) & IRA rollovers",
    ],
  },
  {
    id: "term",
    tag: "TERM",
    name: "Term Life",
    short: "Affordable family protection — customizable to your situation.",
    icon: Shield,
    keyNumbers: [
      { value: "10/20/30", label: "year terms available" },
      { value: "Lowest", label: "premium cost" },
      { value: "Tax-free", label: "death benefit" },
    ],
    detail:
      "Pure protection at the lowest cost. Lock in coverage for 10, 20, or 30 years to protect your income, your mortgage, and your family's future during the years that matter most.",
    howItWorks:
      "You pay a fixed monthly premium for a set term (10, 20, or 30 years). If you pass away during the term, your family receives a tax-free lump sum. If you outlive the term, coverage ends — but many policies can be converted to permanent insurance later without a new medical exam.",
    whoFor: [
      "New parents protecting young children and a working spouse",
      "Homeowners with a mortgage that would crush their family if they died",
      "Anyone with co-signed debt (student loans, business loans, etc.)",
      "Young, healthy adults who want to lock in low rates while they're cheap",
    ],
    problems: [
      "A working parent's death can financially devastate a family for decades",
      "Mortgages, student loans, and childcare don't pause when income stops",
      "Group life through work is rarely enough and disappears when the job does",
      "Permanent insurance is overkill if you just need protection during raising-a-family years",
    ],
    points: [
      "The most affordable way to protect your family",
      "Flexible terms: 10, 20, or 30 years",
      "Coverage amounts tailored to your income & debts",
      "Many policies convertible to permanent coverage later",
    ],
  },
  {
    id: "fex",
    tag: "FEX",
    name: "Final Expense",
    short: "Burial coverage from age 0–89. No medical exam required.",
    icon: HeartHandshake,
    keyNumbers: [
      { value: "0–89", label: "ages eligible" },
      { value: "No exam", label: "simplified approval" },
      { value: "Lifetime", label: "locked-in coverage" },
    ],
    detail:
      "Final expense coverage makes sure your loved ones never carry the burden of funeral costs. Simple application, no medical exam, and coverage that lasts a lifetime.",
    howItWorks:
      "You apply with a short health questionnaire — no medical exam, no blood work. Coverage typically ranges from $5,000 to $50,000 and lasts the rest of your life as long as premiums are paid. When the time comes, benefits are paid quickly — often within days — directly to your beneficiary.",
    whoFor: [
      "Seniors (ages 50–85) who don't want their children burdened with funeral bills",
      "Anyone who's been declined for traditional life insurance due to health",
      "Adults helping aging parents secure simple, no-exam coverage",
      "Families wanting peace of mind that final expenses won't pull from savings",
    ],
    problems: [
      "The average U.S. funeral costs $8,000–$15,000 — paid by family within days",
      "Traditional life insurance gets expensive or impossible after 70",
      "Medical exams can be a barrier for those with health conditions",
      "Without coverage, funeral costs often go on credit cards or GoFundMe",
    ],
    points: [
      "No medical exam — simplified approval",
      "Coverage available from age 0 to 89",
      "Locked-in premiums that never increase",
      "Benefits paid quickly, when family needs it most",
    ],
  },
  {
    id: "infinite-banking",
    tag: "IBC",
    name: "Infinite Banking",
    short: "Become your own bank. Build wealth you control.",
    icon: Infinity,
    keyNumbers: [
      { value: "∞", label: "uninterrupted compounding" },
      { value: "Tax-free", label: "policy loans" },
      { value: "Guaranteed", label: "cash value growth" },
    ],
    detail:
      "The Infinite Banking Concept uses a specially designed whole life policy to let you borrow against your own capital — financing cars, real estate, and opportunities while your money keeps compounding. Wealth on your terms.",
    howItWorks:
      "You fund a dividend-paying whole life policy designed for high cash value. Once cash value builds, you borrow against the policy — not from it — to finance purchases (cars, real estate, business deals). Your full cash value keeps earning dividends and interest while the loan is outstanding, so you compound on uninterrupted capital and pay yourself the interest you'd otherwise pay a bank.",
    whoFor: [
      "Entrepreneurs and real estate investors who finance often",
      "Families building generational wealth and tax-free legacy",
      "High earners who hate paying banks interest on cars and capital purchases",
      "Anyone disciplined enough to treat their own policy like a private bank",
    ],
    problems: [
      "Banks profit on every dollar you finance — that interest could be yours",
      "Traditional savings and money market accounts barely beat inflation",
      "Real estate and business deals often need fast, flexible capital",
      "Most retirement vehicles can't double as a death benefit AND living capital",
    ],
    points: [
      "Borrow against your policy while it keeps growing",
      "Uninterrupted compound growth",
      "Guaranteed cash value, dividends & lifetime protection",
      "A generational wealth strategy used by the wealthy for decades",
    ],
  },
  {
    id: "mortgage-protection",
    tag: "MP",
    name: "Mortgage Protection",
    short: "Coverage so your family keeps the house if the worst happens.",
    icon: Home,
    keyNumbers: [
      { value: "100%", label: "mortgage payoff" },
      { value: "Living", label: "benefits included" },
      { value: "Fast", label: "approval process" },
    ],
    detail:
      "If something happens to you, your family shouldn't lose the home. Mortgage protection pays off or covers the mortgage so the people you love stay where they belong.",
    howItWorks:
      "Mortgage protection is term life insurance structured to match your mortgage. Coverage amount and term mirror your loan, so if you die — or in many cases become disabled or critically ill — the policy pays out enough to cover or pay off the mortgage. Your family stays in the home, full stop.",
    whoFor: [
      "Homeowners with a mortgage and a family who depends on the home",
      "Recently refinanced borrowers who want to update their coverage",
      "Self-employed homeowners without strong employer life benefits",
      "Anyone whose biggest single bill is their mortgage payment",
    ],
    problems: [
      "Losing a primary earner often forces a family to sell their home within a year",
      "Lender-offered mortgage insurance protects the lender, not your family",
      "Disability — not death — is actually the more common cause of mortgage default",
      "PMI is throwaway money; mortgage protection actually pays your family",
    ],
    points: [
      "Pays the mortgage if you pass away or become disabled",
      "Coverage matched to your loan balance & term",
      "Living benefits included on most policies",
      "Fast, simple approval process",
    ],
  },
];

export const BUSINESS_STRATEGIES = [
  {
    id: "key-person",
    tag: "Key Person",
    name: "Key Person Insurance",
    icon: Users,
    keyNumbers: [
      { value: "Tax-free", label: "lump-sum payout" },
      { value: "Business-owned", label: "policy & beneficiary" },
      { value: "6–18 mo", label: "to replace key talent" },
    ],
    summary:
      "A life insurance policy the business owns on an essential employee — the founder, top producer, or technical lead — so the company survives if that person doesn't.",
    howItWorks:
      "The business is the policy owner, premium payer, and beneficiary. It insures a key employee whose loss would materially harm operations. If that person passes away, the business receives a tax-free death benefit it can use to cover lost revenue, recruit a replacement, repay debt, or reassure lenders, customers, and investors.",
    whoFor: [
      "Small businesses where one or two people drive most of the revenue",
      "Founders and CEOs whose departure would shake customer or investor confidence",
      "Companies with bank loans personally guaranteed by an owner",
      "Professional practices (law, medical, financial) built around a rainmaker",
    ],
    problems: [
      "Death of a key employee can crater revenue overnight",
      "Lenders may call loans if a personal guarantor passes",
      "Recruiting and onboarding a replacement takes 6–18 months and serious capital",
      "Customers may flee if they sense leadership instability",
    ],
    benefits: [
      "Tax-free lump sum to the business when it's needed most",
      "Stabilizes operations, payroll, and customer relationships",
      "Provides leverage with lenders and investors",
      "Premiums are a manageable business expense vs. the existential risk",
    ],
  },
  {
    id: "executive-bonus",
    tag: "162 Bonus",
    name: "Executive Bonus Plan",
    icon: Award,
    keyNumbers: [
      { value: "Tax-deductible", label: "premiums for the business" },
      { value: "Employee-owned", label: "cash value & policy" },
      { value: "Selective", label: "no ERISA red tape" },
    ],
    summary:
      "A simple, tax-deductible way for a business to reward and retain top talent: the company pays the premium on a life insurance policy the employee owns.",
    howItWorks:
      "Under a Section 162 Executive Bonus Plan, the employer pays the premium on a life insurance policy (often an IUL) owned by the employee. The premium is tax-deductible to the business and reported as W-2 income to the employee. The employee owns the policy, controls the cash value, and names their own beneficiary — making it a powerful and visible retention tool.",
    whoFor: [
      "Companies that want a selective benefit for owners and top performers — without ERISA red tape",
      "Closely held businesses, professional practices, and family businesses",
      "Founders looking to fund their own retirement through the company",
      "Companies that want simpler administration than a deferred comp plan",
    ],
    problems: [
      "Top employees are recruited away by competitors offering golden handcuffs",
      "Traditional 401(k) plans have tight contribution limits and don't reward selectively",
      "Owners often have no other tax-advantaged retirement vehicle",
      "Bonus cash gets spent — bonus-funded permanent insurance builds long-term wealth",
    ],
    benefits: [
      "Premiums are tax-deductible to the business",
      "Employee owns the policy and accumulates tax-deferred cash value",
      "Highly selective — choose exactly who participates",
      "Acts as a long-term retention incentive without lock-up structures",
    ],
  },
  {
    id: "deferred-compensation",
    tag: "NQDC",
    name: "Deferred Compensation",
    icon: Hourglass,
    summary:
      "A non-qualified plan that lets executives defer compensation today and receive it later — funded with life insurance the business owns.",
    howItWorks:
      "An employer and an executive sign a written agreement: the executive defers a portion of current salary or bonus to be paid out in the future (often at retirement). The business informally funds the future obligation with corporate-owned life insurance — usually an IUL — whose cash value can be accessed to make payments and whose death benefit can recover the company's cost.",
    whoFor: [
      "Companies wanting to retain key executives with golden handcuffs",
      "Highly compensated employees already maxing out 401(k) limits",
      "Owners using NQDC to defer their own income and protect against future tax",
      "Businesses creating tiered benefits that retain succession talent",
    ],
    problems: [
      "401(k)s cap how much high earners can shelter from current tax",
      "Top talent will leave without a clear long-term financial reason to stay",
      "Companies need a way to fund future obligations without straining cash flow",
      "Owners want a tax-deferred bucket beyond qualified retirement plans",
    ],
    benefits: [
      "Defers income — and the tax on it — to a lower-bracket future year",
      "Acts as golden handcuffs: typically requires service to vest and be paid",
      "Cash value inside the policy grows tax-deferred to fund future payouts",
      "Business recovers its cost via the policy's death benefit",
    ],
  },
  {
    id: "buy-sell",
    tag: "Buy-Sell",
    name: "Buy-Sell Agreement",
    icon: Handshake,
    keyNumbers: [
      { value: "Pre-set", label: "ownership valuation" },
      { value: "Instant", label: "liquidity for buyout" },
      { value: "Zero", label: "family/business conflict" },
    ],
    summary:
      "A legally binding plan funded with life insurance that ensures the smooth transfer of a business if an owner dies, becomes disabled, or exits.",
    howItWorks:
      "Co-owners sign a Buy-Sell Agreement that pre-determines how ownership shares will be valued and transferred on death, disability, retirement, or departure. Life insurance funds the agreement: when an owner dies, the policy pays out, the proceeds buy the deceased owner's shares from their estate, and the remaining owners keep full control of the business — without a fire sale or family conflict.",
    whoFor: [
      "Businesses with two or more owners (any structure: LLC, S-Corp, partnership)",
      "Family businesses planning succession across generations",
      "Professional practices (law, medical, accounting) with multiple partners",
      "Any co-owned business where one owner's death would cause chaos",
    ],
    problems: [
      "Without a Buy-Sell, an owner's death can hand voting shares to a spouse or heirs uninvolved in the business",
      "Disputes over valuation can drag on for years and force a forced sale",
      "Remaining owners often don't have the cash to buy out a deceased partner's family quickly",
      "Banks and customers may pull back without clear succession",
    ],
    benefits: [
      "Pre-agreed valuation eliminates conflict at the worst possible time",
      "Life insurance creates the liquidity to actually execute the buyout",
      "Surviving owners keep control; the deceased owner's family gets fair cash",
      "Protects business loans, key customer relationships, and team confidence",
    ],
  },
];

export const STATS = [
  { value: 150, prefix: "$", suffix: "M+", decimals: 0, label: "in retirement funds protected annually" },
  { value: 2000, prefix: "", suffix: "+", decimals: 0, label: "clients served annually" },
  { value: 50, prefix: "", suffix: "+", decimals: 0, label: "A-rated insurance carriers" },
  { value: 0, prefix: "$", suffix: "", decimals: 0, label: "dollars lost to market downturns" },
];

export const TESTIMONIALS = [
  {
    quote:
      "With the IUL I don't have to stress or wonder what my retirement plan will look like. The 401(k) my job provided simply is not enough.",
    name: "Renee H.",
    role: "Client · Planning retirement for her family",
  },
  {
    quote:
      "Breeze walked me through every option in plain English. For the first time I actually understand where my money is going — and my family is protected no matter what happens to me.",
    name: "Marcus T.",
    role: "Client · Father of two",
  },
  {
    quote:
      "After watching my 401(k) drop twice, I moved my savings into a fixed indexed annuity with Breeze. Guaranteed income, zero market stress. I only wish I'd done it sooner.",
    name: "Anthony D.",
    role: "Client · Nearing retirement",
  },
];
